import { useState, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { saveFilterData, setSavedReports } from '../store/slices/filterSlice';
import type { AppDispatch } from '../store/store';
import type { SavedReportsResponse } from '../components/types/types';
import http from '../common/http-auth';
import type {
  FilterData,
  UseSaveFilterReturn,
  SaveFilterResponse,
  logsearchdata,
} from '../components/types/tableauFilter';

export const useSaveFilter = (): UseSaveFilterReturn => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const dispatch = useDispatch<AppDispatch>();

  const saveFilter = useCallback(
    async (dataToSave: FilterData): Promise<void> => {
      setIsLoading(true);
      setError(null);
      try {
        const response = await http.post<SaveFilterResponse>(
          '/dashboard/savedashboardfilters',
          dataToSave,
        );

        if (typeof response.data.data.filterId !== 'string') {
          throw new Error('Invalid response data');
        }

        dispatch(
          saveFilterData({
            filterId: response.data.data.filterId,
            filter: dataToSave.filterData,
          }),
        );
      } catch (err) {
        setError(err instanceof Error ? err.message : 'An error occurred');
      } finally {
        setIsLoading(false);
      }
    },
    [dispatch],
  );

  const savedFilterReports = useCallback(
    async (uid: number): Promise<void> => {
      setIsLoading(true);
      setError(null);
      try {
        const response = await http.get<SavedReportsResponse>(
          `/dashboard/getdashboardfilters?userId=${uid}`,
        );

        if (!Array.isArray(response.data.data)) {
          throw new Error('Invalid response data');
        }

        dispatch(setSavedReports(response.data));
      } catch (err) {
        setError(err instanceof Error ? err.message : 'An error occurred');
      } finally {
        setIsLoading(false);
      }
    },
    [dispatch],
  );
  const logSearchdetails = useCallback(
    async (logSearchdata: logsearchdata): Promise<void> => {
      setIsLoading(true);
      setError(null);
      try {
        const response = await http.post<SaveFilterResponse>(
          '/logsearchdetails/savesearchdetails',
          logSearchdata,
        );

        if (typeof response.data.data.filterId !== 'string') {
          throw new Error('Invalid response data');
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'An error occurred');
      } finally {
        setIsLoading(false);
      }
    },
    [dispatch],
  );

  return { isLoading, error, saveFilter, savedFilterReports, logSearchdetails };
};
